import { memo, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { DatePicker } from '@/components/atoms/DatePicker';
import { Skeleton } from '@/components/atoms/Skeleton';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/store/toastStore';
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Pencil, Check } from 'lucide-react';
import { ServiceNav } from '@/components/organisms/ServiceNav';
import { SEOHead } from '@/components/atoms/SEOHead';

/* ── 타입 ─────────────────────────────── */

interface CalendarEvent {
  id: number;
  category: string;
  title: string;
  description: string | null;
  event_date: string;
  color?: string;
  ticker?: string | null;
  isPublic: boolean;
}

const CATEGORY_STYLE: Record<string, { label: string; bg: string; text: string }> = {
  macro: { label: '경제', bg: 'bg-blue-100', text: 'text-blue-700' },
  earnings: { label: '실적', bg: 'bg-purple-100', text: 'text-purple-700' },
  dividend: { label: '배당', bg: 'bg-green-100', text: 'text-green-700' },
  ipo: { label: 'IPO', bg: 'bg-orange-100', text: 'text-orange-700' },
  dart: { label: '공시 분석', bg: 'bg-rose-100', text: 'text-rose-700' },
  personal: { label: '개인', bg: 'bg-amber-100', text: 'text-amber-700' },
};

/** 나라별(title 기반) 매크로 일정 색상 판별 */
function regionStyleFor(title: string): { bg: string; text: string } | null {
  if (title.includes('미국') || title.includes('FOMC')) {
    return { bg: 'bg-blue-100', text: 'text-blue-700' };
  }
  if (title.includes('한국') || title.includes('BOK') || title.includes('한국은행')) {
    return { bg: 'bg-red-100', text: 'text-red-700' };
  }
  return null;
}

const COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-500',
  red: 'bg-red-500',
  green: 'bg-green-500',
  orange: 'bg-orange-500',
  purple: 'bg-purple-500',
};

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/* ── 유틸 ─────────────────────────────── */

function getMonthKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function todayKstString() {
  const now = new Date();
  const kst = new Date(now.getTime() + (now.getTimezoneOffset() * 60000) + 9 * 3600 * 1000);
  return `${kst.getFullYear()}-${String(kst.getMonth() + 1).padStart(2, '0')}-${String(kst.getDate()).padStart(2, '0')}`;
}

/** 미국 서머타임(EDT): 3월 둘째 일요일 ~ 11월 첫째 일요일 */
function isUsDst(dateStr: string): boolean {
  const [y, m, d] = dateStr.split('-').map(Number);
  // 3월 둘째 일요일
  const marchFirst = new Date(Date.UTC(y, 2, 1));
  const dstStart = new Date(Date.UTC(y, 2, 8 + ((7 - marchFirst.getUTCDay()) % 7)));
  // 11월 첫째 일요일
  const novFirst = new Date(Date.UTC(y, 10, 1));
  const dstEnd = new Date(Date.UTC(y, 10, 1 + ((7 - novFirst.getUTCDay()) % 7)));
  const date = new Date(Date.UTC(y, m - 1, d));
  return date >= dstStart && date < dstEnd;
}

/**
 * 미국 지표 KST 시간 계산
 * - CPI/PPI/고용/GDP/소매/PCE: 미 동부 오전 8:30
 *   EDT → 21:30 KST / EST → 22:30 KST
 * - FOMC 금리: 미 동부 오후 2:00
 *   EDT → 03:00(익일) KST / EST → 04:00(익일) KST
 */
function getKstReleaseTime(title: string, dateStr: string): string | null {
  const isUs = title.startsWith('미국') || title.includes('FOMC');
  if (!isUs) return null;

  const isFomc = title.includes('FOMC');
  const dst = isUsDst(dateStr);

  if (isFomc) {
    return dst ? '한국시간 03:00 (익일)' : '한국시간 04:00 (익일)';
  }

  // 일반 미국 지표 (8:30 ET)
  if (
    title.includes('CPI') || title.includes('PPI') ||
    title.includes('고용') || title.includes('GDP') ||
    title.includes('소매') || title.includes('PCE')
  ) {
    return dst ? '한국시간 21:30' : '한국시간 22:30';
  }

  return null;
}

// 로컬 시간 기준 YYYY-MM-DD 포맷 — toISOString 은 UTC 기준이라 KST 에서 하루 밀림
function fmtLocalDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getCalendarDays(year: number, month: number) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay();
  const days: Array<{ date: string; day: number; isCurrentMonth: boolean; dayOfWeek: number }> = [];

  // 이전 달 빈칸
  for (let i = 0; i < startDay; i++) {
    const d = new Date(year, month, -startDay + i + 1);
    days.push({
      date: fmtLocalDate(d),
      day: d.getDate(),
      isCurrentMonth: false,
      dayOfWeek: d.getDay(),
    });
  }
  // 이번 달
  for (let d = 1; d <= last.getDate(); d++) {
    const dt = new Date(year, month, d);
    days.push({
      date: fmtLocalDate(dt),
      day: d,
      isCurrentMonth: true,
      dayOfWeek: dt.getDay(),
    });
  }
  // 다음 달 빈칸 (6주 채우기)
  while (days.length < 42) {
    const d = new Date(year, month + 1, days.length - startDay - last.getDate() + 1);
    days.push({
      date: fmtLocalDate(d),
      day: d.getDate(),
      isCurrentMonth: false,
      dayOfWeek: d.getDay(),
    });
  }
  return days;
}

/* ── 컴포넌트 ─────────────────────────── */

export function CalendarPage() {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const queryClient = useQueryClient();

  const [current, setCurrent] = useState(() => new Date());
  const [selected, setSelected] = useState<string | null>(() => todayKstString());
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', color: 'green' });
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', color: 'green', eventDate: '' });

  const year = current.getFullYear();
  const month = current.getMonth();
  const monthKey = getMonthKey(current);

  const { data, isLoading } = useQuery({
    queryKey: ['calendar', monthKey],
    queryFn: () => get<{ events: CalendarEvent[] }>(`/calendar/events?month=${monthKey}`),
    staleTime: 5 * 60_000,     // 5분간 fresh — 월 이동 재방문 시 캐시 사용
    gcTime: 30 * 60_000,        // 30분간 캐시 유지
    placeholderData: (prev) => prev, // 월 전환 시 깜빡임 방지
  });

  // 인접 월 프리페치 — 월 이동 시 체감 속도 개선
  useEffect(() => {
    const prevMonth = new Date(year, month - 1, 1);
    const nextMonth = new Date(year, month + 1, 1);
    [prevMonth, nextMonth].forEach((d) => {
      const key = getMonthKey(d);
      queryClient.prefetchQuery({
        queryKey: ['calendar', key],
        queryFn: () => get<{ events: CalendarEvent[] }>(`/calendar/events?month=${key}`),
        staleTime: 5 * 60_000,
      });
    });
  }, [year, month, queryClient]);

  const events = data?.events ?? [];
  const days = useMemo(() => getCalendarDays(year, month), [year, month]);

  // 날짜별 이벤트 맵
  const eventsByDate = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    events.forEach((e) => {
      const list = m.get(e.event_date) ?? [];
      list.push(e);
      m.set(e.event_date, list);
    });
    return m;
  }, [events]);

  const selectedEvents = selected ? (eventsByDate.get(selected) ?? []) : [];
  const todayStr = todayKstString();
  const isTodaySelected = selected === todayStr;

  const addMutation = useMutation({
    mutationFn: () =>
      post('/calendar/events', {
        title: form.title,
        description: form.description || undefined,
        eventDate: selected,
        color: form.color,
      }),
    onSuccess: () => {
      setForm({ title: '', description: '', color: 'green' });
      setAddOpen(false);
      queryClient.invalidateQueries({ queryKey: ['calendar', monthKey] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message ?? '추가 실패'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => del(`/calendar/events/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendar', monthKey] }),
  });

  const editMutation = useMutation({
    mutationFn: () =>
      put(`/calendar/events/${editId}`, {
        title: editForm.title,
        description: editForm.description || undefined,
        eventDate: editForm.eventDate,
        color: editForm.color,
      }),
    onSuccess: () => {
      setEditId(null);
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message ?? '수정 실패'),
  });

  const startEdit = (e: CalendarEvent) => {
    setEditId(e.id);
    setEditForm({
      title: e.title,
      description: e.description ?? '',
      color: e.color ?? 'blue',
      eventDate: e.event_date,
    });
  };

  const prev = () => setCurrent(new Date(year, month - 1, 1));
  const next = () => setCurrent(new Date(year, month + 1, 1));

  return (
    <div className="container-base pt-4 pb-6">
      <SEOHead
        title="투자 캘린더"
        description="국내외 증시 휴장일, FOMC, 실적 발표, 경제지표 발표 일정을 한눈에"
        canonical="/calendar"
      />
      <ServiceNav />
      <h1 className="mb-4 text-2xl font-extrabold">투자 캘린더</h1>

      {/* 월 네비게이션 */}
      <div className="mb-4 flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={prev}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <span className="text-lg font-bold">
          {year}년 {month + 1}월
        </span>
        <Button variant="ghost" size="icon" onClick={next}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
          {/* 요일 헤더 */}
          <div className="grid grid-cols-7 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
            {WEEKDAYS.map((d, i) => (
              <div
                key={d}
                className={`py-2 text-center text-xs font-semibold ${
                  i === 0 ? 'text-red-600' : i === 6 ? 'text-blue-600' : 'text-[var(--color-text)]'
                }`}
              >
                {d}
              </div>
            ))}
          </div>

          {/* 날짜 그리드 */}
          <div className="grid grid-cols-7">
            {days.map((d) => (
              <DayCell
                key={d.date}
                date={d.date}
                day={d.day}
                dayOfWeek={d.dayOfWeek}
                isCurrentMonth={d.isCurrentMonth}
                isSelected={selected === d.date}
                isToday={d.date === todayStr}
                events={eventsByDate.get(d.date) ?? []}
                onClick={() => setSelected(selected === d.date ? null : d.date)}
              />
            ))}
          </div>
        </div>
      )}

      {/* 범례 */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[var(--color-text-sub)]">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 rounded-sm bg-blue-500" />
          미국
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 rounded-sm bg-red-500" />
          한국
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 rounded-sm bg-rose-500" />
          DART 공시
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 rounded-sm bg-green-500" />
          개인 일정
        </span>
      </div>

      {/* 선택 날짜 일정 목록 */}
      {selected && (
        <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-bold">
              {selected}
              {isTodaySelected && (
                <span className="ml-2 rounded bg-[var(--color-primary)] px-1.5 py-0.5 text-[10px] font-bold text-white">
                  오늘
                </span>
              )}
            </h2>
            {isLoggedIn && (
              <Button size="sm" onClick={() => setAddOpen((v) => !v)}>
                {addOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {addOpen ? '닫기' : '일정 추가'}
              </Button>
            )}
          </div>

          {/* 일정 추가 폼 */}
          {addOpen && (
            <div className="mb-4 space-y-2 rounded-lg bg-[var(--color-bg)] p-3">
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="일정 제목"
                maxLength={200}
              />
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="메모 (선택)"
              />
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--color-text-sub)]">색상:</span>
                {(['blue', 'red', 'green', 'orange', 'purple'] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setForm({ ...form, color: c })}
                    className={`h-5 w-5 rounded-full ${COLOR_MAP[c]} ${
                      form.color === c ? 'ring-2 ring-offset-1 ring-[var(--color-text)]' : ''
                    }`}
                  />
                ))}
                <Button
                  size="sm"
                  className="ml-auto"
                  disabled={!form.title.trim()}
                  loading={addMutation.isPending}
                  onClick={() => addMutation.mutate()}
                >
                  추가
                </Button>
              </div>
            </div>
          )}

          {/* 일정 리스트 */}
          {selectedEvents.length > 0 ? (
            <ul className="space-y-2">
              {selectedEvents.map((e) => {
                const baseCat = CATEGORY_STYLE[e.category] ?? CATEGORY_STYLE.personal;
                // macro 는 미국/한국 구분 라벨 + 색상
                let cat = baseCat;
                if (e.category === 'macro') {
                  const region = regionStyleFor(e.title);
                  if (region) {
                    const label = e.title.includes('미국') || e.title.includes('FOMC') ? '미국' : '한국';
                    cat = { ...region, label };
                  }
                }
                const isEditing = editId === e.id && !e.isPublic;

                if (isEditing) {
                  return (
                    <li key={`usr-${e.id}`} className="space-y-2 rounded-lg bg-[var(--color-bg)] p-3">
                      <Input
                        value={editForm.title}
                        onChange={(ev) => setEditForm({ ...editForm, title: ev.target.value })}
                        placeholder="제목"
                      />
                      <Input
                        value={editForm.description}
                        onChange={(ev) => setEditForm({ ...editForm, description: ev.target.value })}
                        placeholder="메모 (선택)"
                      />
                      <DatePicker
                        value={editForm.eventDate}
                        onChange={(v) => setEditForm({ ...editForm, eventDate: v })}
                        ariaLabel="일정 날짜"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[var(--color-text-sub)]">색상:</span>
                        {(['blue', 'red', 'green', 'orange', 'purple'] as const).map((c) => (
                          <button
                            key={c}
                            onClick={() => setEditForm({ ...editForm, color: c })}
                            className={`h-5 w-5 rounded-full ${COLOR_MAP[c]} ${
                              editForm.color === c ? 'ring-2 ring-offset-1 ring-[var(--color-text)]' : ''
                            }`}
                          />
                        ))}
                        <div className="ml-auto flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditId(null)}>
                            취소
                          </Button>
                          <Button
                            size="sm"
                            disabled={!editForm.title.trim()}
                            loading={editMutation.isPending}
                            onClick={() => editMutation.mutate()}
                          >
                            <Check className="h-3.5 w-3.5" />
                            저장
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                }

                return (
                  <li
                    key={`${e.isPublic ? 'pub' : 'usr'}-${e.id}`}
                    className="flex items-start gap-2 rounded-lg bg-[var(--color-bg)] p-3"
                  >
                    <span
                      className={`mt-0.5 inline-block min-w-[32px] shrink-0 rounded px-1.5 py-0.5 text-center text-[10px] font-bold ${cat.bg} ${cat.text}`}
                    >
                      {cat.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{e.title}</div>
                      {e.description && (
                        <div className="mt-0.5 text-xs text-[var(--color-text-sub)]">{e.description}</div>
                      )}
                      {(() => {
                        const kstTime = getKstReleaseTime(e.title, e.event_date);
                        return kstTime ? (
                          <div className="mt-1 inline-flex items-center gap-1 rounded bg-[var(--color-primary)]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-primary)]">
                            🕘 {kstTime}
                          </div>
                        ) : null;
                      })()}
                    </div>
                    {!e.isPublic && (
                      <div className="flex shrink-0 gap-0.5">
                        <button
                          onClick={() => startEdit(e)}
                          className="rounded p-1 text-[var(--color-text-sub)] hover:bg-[var(--color-border)]"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate(e.id)}
                          className="rounded p-1 text-[var(--color-text-sub)] hover:bg-[var(--color-border)]"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="py-6 text-center text-sm text-[var(--color-text-sub)]">
              {isTodaySelected ? '오늘은 예정된 일정이 없어요.' : '이 날짜에 일정이 없어요.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── 메모이즈된 날짜 셀 (42개 리렌더 최소화) ── */

interface DayCellProps {
  date: string;
  day: number;
  dayOfWeek: number;
  isCurrentMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  events: CalendarEvent[];
  onClick: () => void;
}

/** 일정별 좌측 색상 바 스타일 매핑 */
function barStyleFor(e: CalendarEvent): { bg: string; text: string } {
  if (e.category === 'personal') {
    const c = e.color ?? 'green';
    return {
      bg: {
        blue: 'bg-blue-100', red: 'bg-red-100', green: 'bg-green-100',
        orange: 'bg-orange-100', purple: 'bg-purple-100',
      }[c] ?? 'bg-green-100',
      text: {
        blue: 'text-blue-700', red: 'text-red-700', green: 'text-green-700',
        orange: 'text-orange-700', purple: 'text-purple-700',
      }[c] ?? 'text-green-700',
    };
  }
  // 경제 지표(macro)는 나라별로 파랑(미국)/빨강(한국) 구분
  if (e.category === 'macro') {
    const region = regionStyleFor(e.title);
    if (region) return region;
  }
  const cat = CATEGORY_STYLE[e.category];
  return cat ? { bg: cat.bg, text: cat.text } : { bg: 'bg-slate-100', text: 'text-slate-700' };
}

/** 요일 색상 클래스 (현재/흐린 버전 대응) */
function dayColor(dow: number, dimmed: boolean): string {
  if (dow === 0) return dimmed ? 'text-red-300' : 'text-red-600';
  if (dow === 6) return dimmed ? 'text-blue-300' : 'text-blue-600';
  return dimmed ? 'text-[var(--color-text-sub)]/40' : 'text-[var(--color-text)]';
}

const DayCell = memo(function DayCell({
  date, day, dayOfWeek, isCurrentMonth, isSelected, isToday, events, onClick,
}: DayCellProps) {
  const dimmed = !isCurrentMonth;
  /* 지난 날짜 시각적 표시 — 회색 배경만 덮어씀. 클릭/호버/선택은 그대로 동작. */
  const isPast = date < todayKstString();

  return (
    <button
      onClick={onClick}
      className={`flex min-h-[88px] flex-col border-b border-r border-[var(--color-border)] px-1.5 pt-1.5 pb-1 text-left transition-colors ${
        dimmed
          ? 'bg-[var(--color-bg)]/40'
          : isPast
          ? 'bg-[var(--color-bg)]/70'
          : ''
      } ${isSelected ? 'bg-[var(--color-primary)]/5 ring-1 ring-inset ring-[var(--color-primary)]' : 'hover:bg-[var(--color-bg)]'}`}
    >
      {/* 날짜 — 좌측 상단 */}
      <div className="flex">
        <span
          className={`text-xs font-semibold ${
            isToday
              ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-primary)] text-white'
              : dayColor(dayOfWeek, dimmed)
          }`}
        >
          {day}
        </span>
      </div>

      {/* 일정 — 색상 바 (최대 2개 + 추가 개수) */}
      <div className="mt-1 flex flex-col gap-0.5">
        {events.slice(0, 2).map((e, i) => {
          const s = barStyleFor(e);
          return (
            <div
              key={i}
              className={`truncate rounded px-1 py-0.5 text-[10px] font-semibold leading-tight ${s.bg} ${s.text} ${
                dimmed ? 'opacity-60' : ''
              }`}
            >
              {e.title}
            </div>
          );
        })}
        {events.length > 2 && (
          <div className={`px-1 text-[10px] font-semibold text-[var(--color-text-sub)] ${dimmed ? 'opacity-60' : ''}`}>
            +{events.length - 2}개
          </div>
        )}
      </div>
    </button>
  );
});
